import { useState, useEffect } from 'react';

const API = process.env.REACT_APP_API_URL || 'https://thunderbolt-app-production.up.railway.app';

export default function WaitlistPage() {
  const [form, setForm]           = useState({ name: '', email: '', business: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');

  useEffect(() => {
    document.title = 'Thunderbolt — Join the Waitlist';
    return () => { document.title = 'Thunderbolt'; };
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API}/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to join waitlist');
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  }

  const features = [
    {
      icon: <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><rect x="6" y="2" width="10" height="18" rx="2.5" stroke="#007AFF" strokeWidth="1.5"/><rect x="8.5" y="6" width="5" height="4" rx="1" stroke="#007AFF" strokeWidth="1.2" fill="none"/><path d="M8.5 7.5h5" stroke="#007AFF" strokeWidth="1.2" strokeLinecap="round"/><rect x="9" y="12" width="1.5" height="1" rx="0.5" fill="#007AFF"/><circle cx="11" cy="17" r="1" fill="#007AFF"/></svg>,
      text: 'Accept cards from your phone'
    },
    {
      icon: <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><circle cx="11" cy="11" r="8.5" stroke="#FF9500" strokeWidth="1.5" fill="none"/><path d="M7 11l3 3 5-5" stroke="#FF9500" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>,
      text: 'No hardware or setup needed'
    },
    {
      icon: <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><rect x="4" y="13" width="3" height="6" rx="1" fill="#00C48C"/><rect x="9.5" y="9" width="3" height="10" rx="1" fill="#00C48C"/><rect x="15" y="5" width="3" height="14" rx="1" fill="#00C48C"/><path d="M4 10l3.5-3.5M7.5 6.5H5M7.5 6.5V9" stroke="#00C48C" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>,
      text: 'Cash out to your bank instantly'
    },
    {
      icon: <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M9.5 13.5a4 4 0 005.66 0l3-3a4 4 0 00-5.66-5.66L11.5 6" stroke="#1D1D1F" strokeWidth="1.5" strokeLinecap="round" fill="none"/><path d="M12.5 8.5a4 4 0 00-5.66 0l-3 3a4 4 0 005.66 5.66L10.5 16" stroke="#1D1D1F" strokeWidth="1.5" strokeLinecap="round" fill="none"/></svg>,
      text: 'No app required for your customers'
    },
  ];

  return (
    <div style={s.page}>
      <div style={s.card}>

        {/* Logo */}
        <div style={s.logoWrap}>
          <img src="/logo192.png" alt="Thunderbolt" style={s.logo} />
        </div>

        {/* Headline */}
        <h1 style={s.title}>Thunderbolt</h1>
        <p style={s.tagline}>Get paid instantly.</p>
        <p style={s.sub}>Accept payments. No hardware, no setup. Just send a link.</p>

        {!submitted ? (
          <>
            {/* Features */}
            <div style={s.features}>
              {features.map(f => (
                <div key={f.text} style={s.feature}>
                  <span style={s.featureIcon}>{f.icon}</span>
                  <span style={s.featureText}>{f.text}</span>
                </div>
              ))}
            </div>

            <div style={s.divider}/>

            {/* Form */}
            <p style={s.formTitle}>Join the waitlist</p>
            {error && <div style={s.error}>{error}</div>}
            <form onSubmit={handleSubmit}>
              <input
                style={s.input}
                placeholder="Your name"
                value={form.name}
                onChange={e => setForm({...form, name: e.target.value})}
                required
              />
              <input
                style={s.input}
                type="email"
                placeholder="Email address"
                value={form.email}
                onChange={e => setForm({...form, email: e.target.value})}
                required
              />
              <input
                style={s.input}
                placeholder="Business name (optional)"
                value={form.business}
                onChange={e => setForm({...form, business: e.target.value})}
              />
              <button style={s.btn} type="submit" disabled={loading}>
                {loading ? 'Joining...' : 'Request Early Access →'}
              </button>
            </form>
          </>
        ) : (
          <div style={s.success}>
            <div style={s.successIconWrap}>
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="22" stroke="#00C48C" strokeWidth="2"/>
                <path d="M14 24l7 7 13-13" stroke="#00C48C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div style={s.successTitle}>You're on the list!</div>
            <div style={s.successText}>We'll reach out to {form.email} when your account is ready.</div>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page:            { minHeight:'100vh', background:'#F5F5F7', display:'flex', alignItems:'center', justifyContent:'center', padding:20 },
  card:            { background:'#fff', borderRadius:24, padding:40, width:'100%', maxWidth:420, boxShadow:'0 4px 40px rgba(0,0,0,0.08)' },
  logoWrap:        { display:'flex', justifyContent:'center', marginBottom:16 },
  logo:            { width:56, height:56, borderRadius:14 },
  title:           { fontSize:28, fontWeight:700, color:'#1D1D1F', textAlign:'center', marginBottom:6 },
  tagline:         { fontSize:16, fontWeight:600, color:'#007AFF', textAlign:'center', marginBottom:8 },
  sub:             { fontSize:14, color:'#6E6E73', textAlign:'center', lineHeight:1.6, marginBottom:24 },
  features:        { display:'flex', flexDirection:'column', gap:12, marginBottom:24 },
  feature:         { display:'flex', alignItems:'center', gap:12 },
  featureIcon:     { width:32, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 },
  featureText:     { fontSize:14, color:'#1D1D1F', fontWeight:500 },
  divider:         { height:1, background:'#F5F5F7', marginBottom:24 },
  formTitle:       { fontSize:15, fontWeight:600, color:'#1D1D1F', marginBottom:16, textAlign:'center' },
  error:           { background:'#FFF0EF', border:'1px solid #FFD0CE', borderRadius:10, padding:'10px 14px', fontSize:13, color:'#FF3B30', marginBottom:12 },
  input:           { width:'100%', padding:'14px 16px', border:'1.5px solid #E5E5EA', borderRadius:12, fontSize:15, outline:'none', marginBottom:12, boxSizing:'border-box', background:'#F5F5F7' },
  btn:             { width:'100%', padding:16, background:'#007AFF', color:'#fff', border:'none', borderRadius:12, fontSize:15, fontWeight:600, cursor:'pointer', boxShadow:'0 4px 12px rgba(0,122,255,0.3)', display:'block', textAlign:'center', textDecoration:'none', marginTop:4 },
  success:         { textAlign:'center', padding:'20px 0' },
  successIconWrap: { display:'flex', justifyContent:'center', marginBottom:16 },
  successTitle:    { fontSize:22, fontWeight:700, color:'#1D1D1F', marginBottom:8 },
  successText:     { fontSize:14, color:'#6E6E73', lineHeight:1.6 },
};
